import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isLight = !scrolled && isHome;
  const textColor = isLight ? 'text-background-50' : 'text-foreground-800';
  const mutedColor = isLight ? 'text-background-50/80' : 'text-foreground-600';
  const navLinkHover = isLight ? 'hover:text-background-50' : 'hover:text-primary-500';
  const borderColor = isLight ? 'border-background-50/25' : 'border-secondary-200/70';
  const hoverBg = isLight ? 'hover:bg-background-50/10' : 'hover:bg-secondary-50';
  const primaryBtnClass = isLight
    ? 'text-background-50 border border-background-50/30 hover:bg-background-50/15'
    : 'text-background-50 bg-primary-500 hover:bg-primary-600';
  const secondaryBtnClass = isLight
    ? 'text-background-50 border border-background-50/30 hover:bg-background-50/15'
    : 'text-primary-600 border border-primary-300 hover:bg-primary-50';

  const navLinks = [
    { label: 'Home', to: '/' },
    { label: 'Shopping Guide', to: '/basetao-spreadsheet-guide-2026' },
    { label: 'Reviews', href: 'https://kaspreadsheet.com' },
    { label: 'Shipping', href: 'https://kaspreadsheet.com' },
  ];

  const handleCategorySelect = (url: string) => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1280px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-18">
        <Link
          to="/"
          className={`flex items-center gap-2 transition-colors duration-200 ${
            isLight ? 'text-background-50 hover:text-background-50/80' : 'text-foreground-900 hover:text-primary-500'
          }`}
        >
          <span className="text-lg md:text-xl font-heading font-bold tracking-tight whitespace-nowrap">
            Basetao<span className={isLight ? 'text-primary-300' : 'text-primary-500'}>Sheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.to ? (
              <Link
                key={link.label}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${mutedColor} ${navLinkHover}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener nofollow"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${mutedColor} ${navLinkHover}`}
              >
                {link.label}
              </a>
            )
          )}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap ${primaryBtnClass}`}
          >
            Basetao Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap ${secondaryBtnClass}`}
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors duration-200 ${hoverBg}`}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${textColor} ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40 bg-background-50 overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) =>
              link.to ? (
                <Link
                  key={link.label}
                  to={link.to}
                  className="flex items-center px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors duration-150"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors duration-150"
                >
                  {link.label}
                </a>
              )
            )}

            <div className="border-t border-secondary-200/70 my-3"></div>

            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors duration-150"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-primary-500"></i>
                Product Categories
              </span>
              <i className={mobileDropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>

            {mobileDropdownOpen && (
              <div className="ml-3 space-y-0.5 border-l-2 border-secondary-200/60 pl-3">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-500 hover:bg-secondary-50 rounded-md transition-colors duration-150 text-left"
                  >
                    <i className={`${cat.icon} text-base text-primary-500 w-5 h-5 flex items-center justify-center`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            <div className="border-t border-secondary-200/70 my-3"></div>

            <div className="flex flex-col gap-2 px-3 pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors duration-200"
              >
                Basetao Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full text-center px-4 py-3 text-sm font-semibold text-primary-600 border border-primary-300 hover:bg-primary-50 rounded-md transition-colors duration-200"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}